import { NgOptimizedImage } from '@angular/common';
import { Component, OnDestroy, OnInit, Signal, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { AdminMenuService, AdminNotificationService, AdminRoleService } from '@buspro/web/admin/core';
import { AppNotificationDto, LocalUserDto, LocalUserNotificationsApi } from '@buspro/web/apis';
import {
  InAppNotificationService,
  LocalAuthService,
  PusherClientService,
  ResponsiveService,
  TokenService,
  WEB_CONFIG,
  WebAppEnvironment,
} from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { AvatarModule } from 'ngx-avatars';
import { NGXLogger } from 'ngx-logger';
import { Observable, filter, lastValueFrom, take } from 'rxjs';
import {
  AdminSidenavComponent,
  BusinessSwitcherComponent,
  OnlineUsersComponent,
  UserAuthComponent,
} from '../components';

@UntilDestroy()
@Component({
  selector: 'buspro-admin-workspace-layout',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatIconModule,
    MatSidenavModule,
    MatIconButton,
    AdminSidenavComponent,
    RouterOutlet,
    NgOptimizedImage,
    AvatarModule,
    RouterLink,
    FormsModule,
    BusinessSwitcherComponent,
    OnlineUsersComponent,
    UserAuthComponent,
    NzTagComponent,
    NzBadgeModule,
  ],
  providers: [PusherClientService, InAppNotificationService, AdminNotificationService],
  template: `
    <mat-toolbar
      class="mat-elevation-z3 relative z-10 flex flex-row flex-nowrap pr-8"
    >
      <button (click)="collapsed.set(!collapsed())" mat-icon-button>
        <mat-icon>menu</mat-icon>
      </button>
      <div class="w-48 flex items-center cursor-pointer" routerLink="/">
        <img
          [priority]="1"
          ngSrc="/logo-no-border.png"
          alt="Buspro Logo"
          height="188"
          width="600"
        />

        <nz-tag [nzColor]="envColor">{{ envName }}</nz-tag>
      </div>
      <div class="grow"></div>
      <div>
        <buspro-admin-business-switcher></buspro-admin-business-switcher>
      </div>
      <div class="grow"></div>

      <!-- Online Local Users -->
      <buspro-admin-online-users
        [onlineUsers]="onlineLocalUsers()"
        [localUsersData]="localUsersData()"
      ></buspro-admin-online-users>

      <!-- Notification Bell -->
      <div
        routerLink="/notifications"
        class="text-gray-600 text-3xl cursor-pointer hover:bg-gray-100 p-3 rounded-full mr-4"
      >
        <nz-badge [nzCount]="unreadCount" [nzOverflowCount]="10">
          <mat-icon>notifications</mat-icon>
        </nz-badge>
      </div>

      <buspro-admin-user-auth
        (loggingOut)="logout()"
        [currentUser]="currentUser$()!"
      ></buspro-admin-user-auth>
    </mat-toolbar>
    <mat-sidenav-container
      [autosize]="true"
      class="h-[calc(100vh-64px)] overflow-x-hidden bg-gray-100"
    >
      <mat-sidenav
        class="overflow-x-hidden"
        opened
        [mode]="menuSelectedMode()"
        [style.width]="sideNavWidth()"
      >
        <buspro-admin-sidenav [collapsed]="collapsed()"></buspro-admin-sidenav>
      </mat-sidenav>
      <mat-sidenav-content
        [style.margin-left]="sideNavWidth()"
        class="p-4 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-300 overflow-y-auto bg-gray-100"
      >
        <router-outlet></router-outlet>
      </mat-sidenav-content>
    </mat-sidenav-container>

    <!-- Connection Status Indicator -->
    @if (adminNotificationService.connectionState() !== 'connected') {
    <div class="fixed bottom-4 right-4 bg-yellow-100 border-l-4 border-yellow-500 p-4 rounded shadow-lg z-50 max-w-sm">
      <div class="flex items-center">
        <mat-icon class="text-yellow-500 mr-2">warning</mat-icon>
        <span class="text-sm">
          @switch (adminNotificationService.connectionState()) {
            @case ('connecting') {
              Connecting to notifications...
            }
            @case ('disconnected') {
              Notifications disconnected.
              <button (click)="reconnectNotifications()" class="underline ml-2 text-blue-600 hover:text-blue-800">Reconnect</button>
            }
            @case ('failed') {
              Notifications failed to connect.
              <button (click)="reconnectNotifications()" class="underline ml-2 text-blue-600 hover:text-blue-800">Retry</button>
            }
          }
        </span>
      </div>
    </div>
    }
  `,
  styles: `
    mat-sidenav,
    mat-sidenav-content {
      transition: all 300ms ease-in-out;
    }
  `,
})
export class AdminWorkspaceLayoutComponent implements OnInit, OnDestroy {
  private tokenService = inject(TokenService);
  private router = inject(Router);
  private authService = inject(LocalAuthService);
  private responsiveService = inject(ResponsiveService);
  private menuService = inject(AdminMenuService);
  private logger = inject(NGXLogger);
  private ngxAuthService = inject(NgxAuthService);
  private roleService = inject(AdminRoleService);
  private usersNotificationsApi = inject(LocalUserNotificationsApi);
  public adminNotificationService = inject(AdminNotificationService);
  private pusher = inject(PusherClientService);
  public inAppNotificationService = inject(InAppNotificationService);
  protected readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);

  collapsed = this.menuService.collapsed;
  sideNavWidth = this.menuService.sideNavWidth;
  menuSelectedMode: Signal<MatDrawerMode> = computed(() => {
    if (this.responsiveService.smallWidth()) {
      return 'over';
    }
    return 'side';
  });

  currentUser$: Signal<LocalUserDto> = toSignal(this.ngxAuthService.state);
  authUser$: Observable<LocalUserDto | null> = this.ngxAuthService.state;

  // Online users state
  onlineLocalUsers = signal(0);
  localUsersData = signal<any[]>([]);

  notificationsQuery = injectQuery(() => ({
    queryKey: ['notifications', this.currentUser$()?.id],
    queryFn: () => this.loadNotifications(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: false, // Disable polling - use Pusher for real-time updates
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  }));

  get notifications(): AppNotificationDto[] {
    return this.notificationsQuery.data() ?? [];
  }

  get unreadCount(): number {
    return this.notifications.filter((n) => !n.isRead).length;
  }

  get envName() {
    return this.environment.env;
  }

  get envColor() {
    const envName = this.environment.env;
    switch (envName) {
      case 'DEV':
        return 'warning';
      case 'STAGING':
        return 'success';
      case 'PROD':
        return 'processing';
      default:
        return 'warning';
    }
  }

  ngOnInit(): void {
    // Load user roles into ngx-permissions for role-based access control
    this.roleService.loadPermissions();

    this.tokenService.logout$.pipe(untilDestroyed(this)).subscribe(async () => {
      await this.router.navigate(['/passport/login']);
    });

    // Subscribe to admin notifications and presence
    this.authUser$.pipe(untilDestroyed(this)).subscribe((data) => {
      if (data?.id) {
        this.logger.log(`[Admin App] 🔔 Subscribing to admin notifications for user: ${data.id}`);
        this.logger.log(`[Admin App] User details:`, { id: data.id, name: data.firstName, role: data.role });
        this.adminNotificationService.subscribeToAdminNotifications(data.id);

        // Subscribe to presence channels after Pusher connects
        this.pusher.connectionState
          .pipe(
            filter((state) => state === 'connected'),
            take(1),
            untilDestroyed(this),
          )
          .subscribe(() => {
            this.logger.log('[Admin App] 📡 Subscribing to local users presence channel');
            this.pusher.subscribeToLocalUsersPresenceChannel();

            // Note: Admin users cannot subscribe to tenant presence channel due to security restrictions
            // The backend blocks local users from joining the presence-online-users channel
            // Tenant user counts would need to be fetched via a separate API endpoint if needed
          });
      } else {
        this.logger.warn('[Admin App] ⚠️ No user data available for notification subscription');
      }
    });

    // Subscribe to local users presence updates
    this.pusher.localUsersPresence.pipe(untilDestroyed(this)).subscribe((users) => {
      this.logger.debug(`[Admin App] Local users presence updated: ${users.length} users online`);
      this.onlineLocalUsers.set(users.length);
      this.localUsersData.set(users);
    });

    // Initialize browser notification permissions
    this.checkAndInitializeBrowserNotificationFeature();
  }

  ngOnDestroy() {
    this.adminNotificationService.unsubscribeAll();
  }

  loadNotifications(): Promise<AppNotificationDto[]> {
    const userId = this.currentUser$()?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }
    // Use local-user-specific notification endpoint
    return lastValueFrom(
      this.usersNotificationsApi.localUsersNotificationControllerGetUserNotifications({
        id: userId,
      }),
    ).then((result) => result ?? []);
  }

  reconnectNotifications() {
    const userId = this.currentUser$()?.id;
    if (userId) {
      this.adminNotificationService.reconnect(userId);
    }
  }

  private async checkAndInitializeBrowserNotificationFeature(): Promise<void> {
    if (!('Notification' in window)) {
      this.logger.warn('This browser does not support desktop notification');
      return;
    }

    this.logger.log(`Current notification permission: ${Notification.permission}`);

    if (Notification.permission === 'granted') {
      this.logger.log('✅ Notification permission already granted');
    } else if (Notification.permission === 'denied') {
      this.logger.warn('❌ Notification permission denied by user. Please enable it in browser settings.');
    } else if (Notification.permission === 'default') {
      this.logger.log('⚠️ Notification permission not yet requested. Requesting now...');
      const permission = await Notification.requestPermission();
      this.logger.log(`Notification permission result: ${permission}`);

      if (permission === 'granted') {
        this.logger.log('✅ Notification permission granted!');
      } else if (permission === 'denied') {
        this.logger.warn('❌ User denied notification permission');
      }
    }
  }

  /**
   * Manual method to request notification permission
   * Useful for debugging
   */
  async requestNotificationPermission(): Promise<void> {
    if (!('Notification' in window)) {
      this.logger.error('This browser does not support desktop notifications');
      alert('This browser does not support desktop notifications');
      return;
    }

    this.logger.log(`Current permission: ${Notification.permission}`);

    if (Notification.permission === 'denied') {
      this.logger.warn('Notification permission was denied. Please enable it in browser settings.');
      alert('Notification permission was denied. Please enable it in your browser settings.');
      return;
    }

    if (Notification.permission === 'granted') {
      this.logger.log('Notification permission already granted');

      // Show test notification
      const testNotification = new Notification('Test Notification', {
        body: 'Browser notifications are working correctly!',
        icon: '/favicon.png',
        requireInteraction: false,
      });

      testNotification.onclick = () => {
        window.focus();
        testNotification.close();
      };

      return;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    this.logger.log(`Permission request result: ${permission}`);

    if (permission === 'granted') {
      this.logger.log('✅ Permission granted! Showing test notification...');

      const testNotification = new Notification('Success!', {
        body: 'Browser notifications are now enabled!',
        icon: '/favicon.png',
        requireInteraction: false,
      });

      testNotification.onclick = () => {
        window.focus();
        testNotification.close();
      };
    } else {
      alert('Notification permission was denied');
    }
  }

  async logout() {
    await this.ngxAuthService.logout();
    await this.authService.logout();
  }
}
